import { devLog } from '../utils/logger';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProfessionalCard from '../components/ProfessionalCard';
import { useAuth, getAuthHeaders } from '../contexts/AuthContext';
import { 
  Sparkles, ArrowRight, ArrowLeft, Loader2, Heart, Briefcase,
  CheckCircle, Target, Brain, User, Monitor, Clock, Shield
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 'concern', title: 'Sua Preocupação', description: 'Qual é a sua principal preocupação ou desafio?' },
  { id: 'experience', title: 'Sua Experiência', description: 'Conte-nos sobre sua experiência com terapias' },
  { id: 'methods', title: 'Métodos de Interesse', description: 'Quais abordagens você gostaria de explorar?' },
  { id: 'preferences', title: 'Suas Preferências', description: 'Como você prefere ser atendido?' },
  { id: 'goals', title: 'Suas Expectativas', description: 'O que você espera alcançar?' }
];

const mainConcerns = [
  { id: 'anxiety', label: 'Ansiedade', icon: '💭' },
  { id: 'stress', label: 'Estresse', icon: '⚡' },
  { id: 'depression', label: 'Tristeza / Depressão', icon: '🌧' },
  { id: 'relationships', label: 'Relacionamentos', icon: '💞' },
  { id: 'self_esteem', label: 'Autoestima', icon: '🪞' },
  { id: 'career', label: 'Carreira / Propósito', icon: '🎯' },
  { id: 'physical', label: 'Dores Físicas', icon: '🩹' },
  { id: 'emotional', label: 'Problemas Emocionais', icon: '🌊' },
  { id: 'spiritual', label: 'Busca Espiritual', icon: '✨' },
  { id: 'trauma', label: 'Trauma / Luto', icon: '🕊' },
  { id: 'sleep', label: 'Insônia / Sono', icon: '🌙' },
  { id: 'self_knowledge', label: 'Autoconhecimento', icon: '🧭' }
];

const experienceOptions = [
  { id: 'none', label: 'Nunca fiz nenhum tipo de terapia', description: 'Será minha primeira experiência' },
  { id: 'traditional', label: 'Já fiz terapia convencional', description: 'Psicologia, psicoterapia, etc.' },
  { id: 'holistic', label: 'Já experimentei terapias holísticas', description: 'Reiki, acupuntura, meditação, etc.' },
  { id: 'both', label: 'Já fiz ambos', description: 'Terapia convencional e holística' }
];

const methodOptions = [
  { id: 'tcc', label: 'Terapia Cognitivo-Comportamental', category: 'therapist' },
  { id: 'psychoanalysis', label: 'Psicanálise', category: 'therapist' },
  { id: 'mindfulness', label: 'Mindfulness / Meditação', category: 'therapist' },
  { id: 'gestalt', label: 'Gestalt', category: 'therapist' },
  { id: 'mentoring', label: 'Mentoria / Coaching', category: 'coach' },
  { id: 'nlp', label: 'PNL (Programação Neurolinguística)', category: 'coach' },
  { id: 'career_coaching', label: 'Coaching de Carreira', category: 'coach' },
  { id: 'reiki', label: 'Reiki / Energia', category: 'spiritualist' },
  { id: 'tarot', label: 'Tarot / Oráculo', category: 'spiritualist' },
  { id: 'aromatherapy', label: 'Aromaterapia / Florais', category: 'spiritualist' },
  { id: 'acupuncture', label: 'Acupuntura', category: 'spiritualist' },
  { id: 'constellation', label: 'Constelação Familiar', category: 'spiritualist' },
  { id: 'yoga', label: 'Yoga Terapêutico', category: 'spiritualist' },
  { id: 'open', label: 'Estou aberto(a) a sugestões', category: 'all' }
];

const approachPreferences = [
  { id: 'practical', label: 'Direta e prática', description: 'Prefiro uma abordagem focada em resultados e técnicas' },
  { id: 'intuitive', label: 'Intuitiva e espiritual', description: 'Prefiro uma abordagem mais sensível e conectada' },
  { id: 'balanced', label: 'Equilibrada', description: 'Um pouco de cada, depende da situação' }
];

const frequencyOptions = [
  { id: 'weekly', label: 'Semanal' },
  { id: 'biweekly', label: 'Quinzenal' },
  { id: 'monthly', label: 'Mensal' },
  { id: 'flexible', label: 'Flexível / Sob demanda' }
];

const genderPreferences = [
  { id: 'no_preference', label: 'Sem preferência' },
  { id: 'female', label: 'Prefiro mulher' },
  { id: 'male', label: 'Prefiro homem' }
];

const modalityOptions = [
  { id: 'online', label: 'Online (videochamada/chat)', icon: Monitor },
  { id: 'any', label: 'Tanto faz', icon: CheckCircle }
];

function OptionCard({ selected, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
        selected
          ? 'border-[#4A5D4E] bg-[#4A5D4E]/5'
          : 'border-[#EAE8E3] hover:border-[#4A5D4E]/40 bg-white'
      }`}
    >
      {children}
    </button>
  );
}

function ChipSelect({ options, selected, onToggle, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt.id}
          onClick={() => onToggle(opt.id)}
          data-testid={`${testIdPrefix}-${opt.id}`}
          className={`px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
            selected.includes(opt.id)
              ? 'bg-[#4A5D4E] text-white'
              : 'bg-[#EAE8E3] text-[#63635E] hover:bg-[#D4A373]/20'
          }`}
        >
          {opt.icon && <span className="mr-1.5">{opt.icon}</span>}
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export default function MatchPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [matchResults, setMatchResults] = useState(null);

  const [formData, setFormData] = useState({
    concerns: [],
    experience: '',
    methods: [],
    approach_preference: '',
    frequency: '',
    gender_preference: 'no_preference',
    modality: 'online',
    goals: '',
    allergies_notes: ''
  });

  const toggleArray = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter(v => v !== value)
        : [...prev[field], value]
    }));
  };

  const setField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0: return formData.concerns.length > 0;
      case 1: return formData.experience !== '';
      case 2: return formData.methods.length > 0;
      case 3: return formData.approach_preference !== '';
      case 4: return true;
      default: return false;
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) setCurrentStep(currentStep - 1);
  };

  const deriveMatchData = () => {
    const methodCategories = formData.methods.map(m => {
      const method = methodOptions.find(mo => mo.id === m);
      return method?.category;
    }).filter(Boolean);

    const professional_type = [...new Set(methodCategories.filter(c => c !== 'all'))];
    if (professional_type.length === 0) professional_type.push('therapist', 'coach', 'spiritualist');

    const concernLabels = formData.concerns.map(c => mainConcerns.find(mc => mc.id === c)?.label).filter(Boolean);
    const methodLabels = formData.methods.map(m => methodOptions.find(mo => mo.id === m)?.label).filter(Boolean);
    const approachLabel = approachPreferences.find(a => a.id === formData.approach_preference)?.label || '';
    const frequencyLabel = frequencyOptions.find(f => f.id === formData.frequency)?.label || '';
    const expLabel = experienceOptions.find(e => e.id === formData.experience)?.label || '';

    return {
      professional_type,
      interests: concernLabels,
      therapy_style: methodLabels,
      goals: `Preocupações: ${concernLabels.join(', ')}. Experiência: ${expLabel}. Abordagem preferida: ${approachLabel}. Frequência: ${frequencyLabel}. Preferência de gênero: ${formData.gender_preference === 'no_preference' ? 'sem preferência' : formData.gender_preference}. Modalidade: ${formData.modality}. Objetivos: ${formData.goals || 'Não especificado'}. ${formData.allergies_notes ? 'Notas: ' + formData.allergies_notes : ''}`
    };
  };

  const handleSubmit = async () => {
    if (!user) { navigate('/login'); return; }
    setLoading(true);
    try {
      const matchData = deriveMatchData();
      const { data } = await axios.post(`${API_URL}/api/match`, matchData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setMatchResults(data);
    } catch (err) {
      devLog.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetMatch = () => {
    setMatchResults(null);
    setCurrentStep(0);
    setFormData({
      concerns: [], experience: '', methods: [], approach_preference: '',
      frequency: '', gender_preference: 'no_preference', modality: 'online',
      goals: '', allergies_notes: ''
    });
  };

  if (matchResults) {
    return (
      <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
        <Header />
        <main className="flex-1 py-12" data-testid="match-results">
          <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
            <div className="text-center mb-12 animate-fade-in">
              <div className="w-16 h-16 mx-auto rounded-full bg-[#4A5D4E]/10 flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-[#4A5D4E]" strokeWidth={1.5} />
              </div>
              <h1 className="text-3xl sm:text-4xl font-light text-[#292927] mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Seus Matches
              </h1>
              {matchResults.ai_powered && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F6EBE6] text-[#4A5D4E] text-sm font-medium mb-4">
                  <Brain className="w-4 h-4" strokeWidth={1.5} />
                  Powered by AI
                </div>
              )}
              {matchResults.ai_recommendation && (
                <p className="text-[#63635E] max-w-2xl mx-auto">{matchResults.ai_recommendation}</p>
              )}
            </div>

            {matchResults.matches?.length > 0 ? (
              <div className="space-y-6">
                {matchResults.matches.map((prof, idx) => (
                  <div key={prof.id} className="animate-fade-in" style={{ animationDelay: `${idx * 0.1}s` }}>
                    <ProfessionalCard professional={prof} showMatchReason={true} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-[#63635E]">Nenhum profissional encontrado com os critérios selecionados.</p>
              </div>
            )}

            <div className="mt-12 text-center">
              <button onClick={resetMatch} className="btn-secondary" data-testid="new-match-button">
                Fazer novo match
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F8F6]">
      <Header />
      <main className="flex-1 py-12" data-testid="match-page">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Progress */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-2">
              {steps.map((step, idx) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      idx <= currentStep ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#A0A09A]'
                    }`}>
                      {idx < currentStep ? <CheckCircle className="w-4 h-4" strokeWidth={2} /> : idx + 1}
                    </div>
                    <span className="hidden sm:block mt-1.5 text-[10px] text-[#63635E] max-w-[70px] text-center leading-tight">{step.title}</span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={`flex-1 h-1 mx-1.5 rounded-full transition-colors ${idx < currentStep ? 'bg-[#4A5D4E]' : 'bg-[#EAE8E3]'}`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Step Content */}
          <div className="ai-matching-card" data-testid="step-content">
            <div className="absolute inset-0 animate-shimmer opacity-30" />
            <div className="relative z-10">
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-light text-[#292927] mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {steps[currentStep].title}
                </h2>
                <p className="text-[#63635E]">{steps[currentStep].description}</p>
              </div>

              {/* Step 0: Main Concerns */}
              {currentStep === 0 && (
                <div data-testid="step-concerns">
                  <p className="text-sm text-[#A0A09A] mb-4">Selecione todas que se aplicam</p>
                  <ChipSelect
                    options={mainConcerns}
                    selected={formData.concerns}
                    onToggle={(id) => toggleArray('concerns', id)}
                    testIdPrefix="concern"
                  />
                </div>
              )}

              {/* Step 1: Experience */}
              {currentStep === 1 && (
                <div className="space-y-3" data-testid="step-experience">
                  {experienceOptions.map((opt) => (
                    <OptionCard
                      key={opt.id}
                      selected={formData.experience === opt.id}
                      onClick={() => setField('experience', opt.id)}
                      testId={`exp-${opt.id}`}
                    >
                      <p className="font-medium text-[#292927]">{opt.label}</p>
                      <p className="text-sm text-[#63635E] mt-0.5">{opt.description}</p>
                    </OptionCard>
                  ))}
                </div>
              )}

              {/* Step 2: Methods */}
              {currentStep === 2 && (
                <div data-testid="step-methods">
                  <p className="text-sm text-[#A0A09A] mb-4">Selecione os métodos que mais te interessam</p>
                  <div className="space-y-6">
                    <div>
                      <p className="text-xs font-medium text-[#4A5D4E] uppercase tracking-wider mb-2">Terapia</p>
                      <ChipSelect
                        options={methodOptions.filter(m => m.category === 'therapist')}
                        selected={formData.methods}
                        onToggle={(id) => toggleArray('methods', id)}
                        testIdPrefix="method"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#D4A373] uppercase tracking-wider mb-2">Mentoria</p>
                      <ChipSelect
                        options={methodOptions.filter(m => m.category === 'coach')}
                        selected={formData.methods}
                        onToggle={(id) => toggleArray('methods', id)}
                        testIdPrefix="method"
                      />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-[#9B8BAE] uppercase tracking-wider mb-2">Espiritual</p>
                      <ChipSelect
                        options={methodOptions.filter(m => m.category === 'spiritualist')}
                        selected={formData.methods}
                        onToggle={(id) => toggleArray('methods', id)}
                        testIdPrefix="method"
                      />
                    </div>
                    <div className="pt-2 border-t border-[#EAE8E3]">
                      <ChipSelect
                        options={methodOptions.filter(m => m.category === 'all')}
                        selected={formData.methods}
                        onToggle={(id) => toggleArray('methods', id)}
                        testIdPrefix="method"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Preferences */}
              {currentStep === 3 && (
                <div className="space-y-8" data-testid="step-preferences">
                  {/* Approach */}
                  <div>
                    <p className="text-sm font-medium text-[#292927] mb-3 flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#4A5D4E]" />
                      Preferência de abordagem
                    </p>
                    <div className="space-y-2">
                      {approachPreferences.map((opt) => (
                        <OptionCard
                          key={opt.id}
                          selected={formData.approach_preference === opt.id}
                          onClick={() => setField('approach_preference', opt.id)}
                          testId={`approach-${opt.id}`}
                        >
                          <p className="font-medium text-[#292927] text-sm">{opt.label}</p>
                          <p className="text-xs text-[#63635E]">{opt.description}</p>
                        </OptionCard>
                      ))}
                    </div>
                  </div>

                  {/* Frequency */}
                  <div>
                    <p className="text-sm font-medium text-[#292927] mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#4A5D4E]" />
                      Frequência desejada
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {frequencyOptions.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setField('frequency', opt.id)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            formData.frequency === opt.id
                              ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#63635E] hover:bg-[#D4A373]/20'
                          }`}
                          data-testid={`freq-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Gender */}
                  <div>
                    <p className="text-sm font-medium text-[#292927] mb-3 flex items-center gap-2">
                      <User className="w-4 h-4 text-[#4A5D4E]" />
                      Preferência de gênero do profissional
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {genderPreferences.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setField('gender_preference', opt.id)}
                          className={`px-4 py-2 rounded-full text-sm transition-all ${
                            formData.gender_preference === opt.id
                              ? 'bg-[#4A5D4E] text-white' : 'bg-[#EAE8E3] text-[#63635E] hover:bg-[#D4A373]/20'
                          }`}
                          data-testid={`gender-${opt.id}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Modality */}
                  <div>
                    <p className="text-sm font-medium text-[#292927] mb-3 flex items-center gap-2">
                      <Monitor className="w-4 h-4 text-[#4A5D4E]" />
                      Modalidade de atendimento
                    </p>
                    <div className="flex gap-3">
                      {modalityOptions.map((opt) => (
                        <button
                          key={opt.id}
                          onClick={() => setField('modality', opt.id)}
                          className={`flex-1 p-3 rounded-xl border-2 text-center text-sm transition-all ${
                            formData.modality === opt.id
                              ? 'border-[#4A5D4E] bg-[#4A5D4E]/5' : 'border-[#EAE8E3] hover:border-[#4A5D4E]/40'
                          }`}
                          data-testid={`modality-${opt.id}`}
                        >
                          <opt.icon className="w-5 h-5 mx-auto mb-1 text-[#4A5D4E]" strokeWidth={1.5} />
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 4: Goals */}
              {currentStep === 4 && (
                <div className="space-y-6" data-testid="step-goals">
                  <div>
                    <div className="flex items-start gap-3 mb-3 p-3 bg-white/50 rounded-xl">
                      <Target className="w-5 h-5 text-[#4A5D4E] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-sm text-[#63635E]">
                        Descreva suas expectativas e objetivos com a terapia. Quanto mais detalhes, melhor o match.
                      </p>
                    </div>
                    <Textarea
                      value={formData.goals}
                      onChange={(e) => setField('goals', e.target.value)}
                      placeholder="Ex: Quero desenvolver mais autoconfiança, lidar melhor com a ansiedade no trabalho e melhorar meus relacionamentos..."
                      className="input-theraflow min-h-[120px]"
                      data-testid="goals-input"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#292927] mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4 text-[#4A5D4E]" />
                      Alguma alergia, restrição ou observação importante? <span className="text-[#A0A09A] font-normal">(opcional)</span>
                    </p>
                    <Textarea
                      value={formData.allergies_notes}
                      onChange={(e) => setField('allergies_notes', e.target.value)}
                      placeholder="Ex: Tenho alergia a óleos essenciais, prefiro sessões no período da manhã..."
                      className="input-theraflow min-h-[80px]"
                      data-testid="notes-input"
                    />
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#EAE8E3]">
                <button
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    currentStep === 0 ? 'text-[#A0A09A] cursor-not-allowed' : 'text-[#63635E] hover:text-[#292927]'
                  }`}
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                  Voltar
                </button>

                <button
                  onClick={handleNext}
                  disabled={!canProceed() || loading}
                  className="btn-primary flex items-center gap-2"
                  data-testid="next-button"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : currentStep === steps.length - 1 ? (
                    <>
                      <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                      Encontrar Match
                    </>
                  ) : (
                    <>
                      Próximo
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </>
                  )}
                </button>
              </div>

              {!user && currentStep === steps.length - 1 && (
                <p className="mt-4 text-center text-sm text-[#A0A09A]">
                  Você precisará fazer login para ver os resultados
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
